import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import LanguagePicker from "@/components/LanguagePicker";

const lnk = (href, text, external = false) => (
    <a href={href} className="text-[#0b905d] hover:underline" {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
        {text}
    </a>
);

const CONTENT = {
    de: {
        eyebrow: "Rechtliches",
        title: "Impressum",
        subtitle: "Angaben gemäß § 5 DDG (Digitale-Dienste-Gesetz) und § 18 MStV.",
        back: "Zurück",
        sections: [
            {
                title: "Diensteanbieter",
                body: (<><p><strong>SideQuestGuy.com</strong></p><p>Inhaber: Arno Strehle</p><p>Stresemannplatz 2</p><p>01309 Dresden</p><p>Deutschland</p></>),
            },
            {
                title: "Kontakt",
                body: (<><p>Telefon: {lnk("tel:+4935182489029", "+49 351 82489029")}</p><p>E-Mail: {lnk("mailto:kontakt@widerruf.online", "kontakt@widerruf.online")}</p></>),
            },
            {
                title: "Umsatzsteuer",
                body: <p>Gemäß § 19 UStG (Kleinunternehmerregelung) wird keine Umsatzsteuer ausgewiesen. Eine Umsatzsteuer-Identifikationsnummer liegt nicht vor.</p>,
            },
            {
                title: "Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV",
                body: (<><p>Arno Strehle</p><p>Stresemannplatz 2</p><p>01309 Dresden</p></>),
            },
            {
                title: "EU-Streitschlichtung",
                body: (<><p>Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}{lnk("https://ec.europa.eu/consumers/odr/", "https://ec.europa.eu/consumers/odr/", true)}.</p><p className="mt-2">Unsere E-Mail-Adresse finden Sie oben im Impressum.</p></>),
            },
            {
                title: "Verbraucherstreitbeilegung / Universalschlichtungsstelle",
                body: <p>Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>,
            },
            {
                title: "Haftung für Inhalte",
                body: <p>Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.</p>,
            },
            {
                title: "Haftung für Links",
                body: <p>Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.</p>,
            },
            {
                title: "Urheberrecht",
                body: <p>Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.</p>,
            },
        ],
    },
    en: {
        eyebrow: "Legal",
        title: "Legal Notice",
        subtitle: "Information pursuant to § 5 DDG (Digital Services Act) and § 18 MStV (State Media Treaty).",
        back: "Back",
        sections: [
            {
                title: "Service Provider",
                body: (<><p><strong>SideQuestGuy.com</strong></p><p>Owner: Arno Strehle</p><p>Stresemannplatz 2</p><p>01309 Dresden</p><p>Germany</p></>),
            },
            {
                title: "Contact",
                body: (<><p>Phone: {lnk("tel:+4935182489029", "+49 351 82489029")}</p><p>Email: {lnk("mailto:kontakt@widerruf.online", "kontakt@widerruf.online")}</p></>),
            },
            {
                title: "VAT",
                body: <p>In accordance with § 19 UStG (small business regulation), no VAT is charged. No VAT identification number is available.</p>,
            },
            {
                title: "Responsible for Content pursuant to § 18 para. 2 MStV",
                body: (<><p>Arno Strehle</p><p>Stresemannplatz 2</p><p>01309 Dresden, Germany</p></>),
            },
            {
                title: "EU Dispute Resolution",
                body: (<><p>The European Commission provides a platform for online dispute resolution (ODR):{" "}{lnk("https://ec.europa.eu/consumers/odr/", "https://ec.europa.eu/consumers/odr/", true)}.</p><p className="mt-2">Our email address can be found above in the Legal Notice.</p></>),
            },
            {
                title: "Consumer Dispute Resolution",
                body: <p>We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.</p>,
            },
            {
                title: "Liability for Content",
                body: <p>As a service provider, we are responsible for our own content on these pages in accordance with general law pursuant to § 7 para. 1 DDG. However, pursuant to §§ 8 to 10 DDG, we are not obligated as a service provider to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.</p>,
            },
            {
                title: "Liability for Links",
                body: <p>Our offer contains links to external websites of third parties, on whose content we have no influence. We cannot therefore accept any liability for these external contents. The provider or operator of the respective linked pages is always responsible for their content.</p>,
            },
            {
                title: "Copyright",
                body: <p>The content and works created by the site operators on these pages are subject to German copyright law. Duplication, processing, distribution and any kind of exploitation outside the limits of copyright law require the written consent of the respective author or creator.</p>,
            },
        ],
    },
    fr: {
        eyebrow: "Mentions légales",
        title: "Mentions légales",
        subtitle: "Informations conformément au § 5 DDG (loi sur les services numériques) et au § 18 MStV.",
        back: "Retour",
        sections: [
            {
                title: "Prestataire de services",
                body: (<><p><strong>SideQuestGuy.com</strong></p><p>Propriétaire : Arno Strehle</p><p>Stresemannplatz 2</p><p>01309 Dresde</p><p>Allemagne</p></>),
            },
            {
                title: "Contact",
                body: (<><p>Téléphone : {lnk("tel:+4935182489029", "+49 351 82489029")}</p><p>E-mail : {lnk("mailto:kontakt@widerruf.online", "kontakt@widerruf.online")}</p></>),
            },
            {
                title: "TVA",
                body: <p>Conformément au § 19 UStG (régime des petites entreprises), la TVA n'est pas facturée. Aucun numéro d'identification TVA n'est disponible.</p>,
            },
            {
                title: "Responsable du contenu conformément au § 18 al. 2 MStV",
                body: (<><p>Arno Strehle</p><p>Stresemannplatz 2</p><p>01309 Dresde, Allemagne</p></>),
            },
            {
                title: "Résolution des litiges en ligne (UE)",
                body: (<><p>La Commission européenne met à disposition une plateforme de résolution des litiges en ligne (RLL) :{" "}{lnk("https://ec.europa.eu/consumers/odr/", "https://ec.europa.eu/consumers/odr/", true)}.</p><p className="mt-2">Notre adresse e-mail figure dans les mentions légales ci-dessus.</p></>),
            },
            {
                title: "Règlement des litiges avec les consommateurs",
                body: <p>Nous ne sommes ni disposés ni tenus de participer à une procédure de règlement des litiges devant une commission d'arbitrage à la consommation.</p>,
            },
            {
                title: "Responsabilité pour le contenu",
                body: <p>En tant que prestataire de services, nous sommes responsables de nos propres contenus sur ces pages conformément aux lois générales en vertu du § 7 al. 1 DDG. En revanche, conformément aux §§ 8 à 10 DDG, nous ne sommes pas tenus de surveiller les informations de tiers transmises ou stockées ni de rechercher des circonstances indiquant une activité illicite.</p>,
            },
            {
                title: "Responsabilité pour les liens",
                body: <p>Notre offre contient des liens vers des sites web externes de tiers sur le contenu desquels nous n'avons aucune influence. Nous ne pouvons donc pas assumer de responsabilité pour ces contenus externes. Le fournisseur ou l'exploitant des pages liées est toujours responsable de leur contenu.</p>,
            },
            {
                title: "Droits d'auteur",
                body: <p>Les contenus et œuvres créés par les opérateurs du site sur ces pages sont soumis au droit d'auteur allemand. La duplication, le traitement, la diffusion et toute forme d'exploitation en dehors des limites du droit d'auteur nécessitent le consentement écrit de l'auteur ou du créateur concerné.</p>,
            },
        ],
    },
    es: {
        eyebrow: "Legal",
        title: "Aviso legal",
        subtitle: "Información conforme al § 5 DDG (Ley de Servicios Digitales) y al § 18 MStV.",
        back: "Volver",
        sections: [
            {
                title: "Proveedor de servicios",
                body: (<><p><strong>SideQuestGuy.com</strong></p><p>Titular: Arno Strehle</p><p>Stresemannplatz 2</p><p>01309 Dresde</p><p>Alemania</p></>),
            },
            {
                title: "Contacto",
                body: (<><p>Teléfono: {lnk("tel:+4935182489029", "+49 351 82489029")}</p><p>Correo electrónico: {lnk("mailto:kontakt@widerruf.online", "kontakt@widerruf.online")}</p></>),
            },
            {
                title: "IVA",
                body: <p>De conformidad con el § 19 UStG (régimen de pequeña empresa), no se aplica IVA. No se dispone de número de identificación fiscal a efectos del IVA.</p>,
            },
            {
                title: "Responsable del contenido conforme al § 18 párr. 2 MStV",
                body: (<><p>Arno Strehle</p><p>Stresemannplatz 2</p><p>01309 Dresde, Alemania</p></>),
            },
            {
                title: "Resolución de disputas en línea (UE)",
                body: (<><p>La Comisión Europea pone a disposición una plataforma de resolución de litigios en línea (ODR):{" "}{lnk("https://ec.europa.eu/consumers/odr/", "https://ec.europa.eu/consumers/odr/", true)}.</p><p className="mt-2">Nuestra dirección de correo electrónico figura en el aviso legal anterior.</p></>),
            },
            {
                title: "Resolución de disputas con consumidores",
                body: <p>No estamos dispuestos ni obligados a participar en procedimientos de resolución de disputas ante una junta de arbitraje de consumidores.</p>,
            },
            {
                title: "Responsabilidad por el contenido",
                body: <p>Como proveedor de servicios, somos responsables de nuestros propios contenidos en estas páginas conforme a la ley general según el § 7 párr. 1 DDG. Sin embargo, de conformidad con los §§ 8 a 10 DDG, no estamos obligados a supervisar la información de terceros transmitida o almacenada ni a investigar circunstancias que indiquen una actividad ilegal.</p>,
            },
            {
                title: "Responsabilidad por los enlaces",
                body: <p>Nuestra oferta contiene enlaces a sitios web externos de terceros sobre cuyo contenido no tenemos ninguna influencia. Por ello, no podemos asumir ninguna responsabilidad por estos contenidos externos. El proveedor u operador de las páginas enlazadas es siempre responsable de su contenido.</p>,
            },
            {
                title: "Derechos de autor",
                body: <p>Los contenidos y obras creados por los operadores del sitio en estas páginas están sujetos a la ley alemana de derechos de autor. La duplicación, el tratamiento, la distribución y cualquier tipo de explotación fuera de los límites de la ley de derechos de autor requieren el consentimiento por escrito del autor o creador respectivo.</p>,
            },
        ],
    },
};

export default function Impressum() {
    const { lang } = useI18n();
    const c = CONTENT[lang] || CONTENT.de;

    return (
        <div className="min-h-screen bg-white" data-testid="impressum-page">
            <header className="border-b border-gray-200 bg-white/80 backdrop-blur sticky top-0 z-30">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-md bg-[#0b905d] flex items-center justify-center">
                            <span className="text-white font-display font-bold">W</span>
                        </div>
                        <span className="font-display font-bold text-lg tracking-tight text-[#00153f]">
                            widerruf<span className="text-[#0b905d] text-sm font-semibold">.online</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <LanguagePicker />
                        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#00153f]" data-testid="back-home">
                            <ArrowLeft className="w-4 h-4" /> {c.back}
                        </Link>
                    </div>
                </div>
            </header>

            <article className="max-w-3xl mx-auto px-6 py-16">
                <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-3">{c.eyebrow}</div>
                <h1 className="font-display text-4xl font-bold tracking-tight text-[#00153f]">{c.title}</h1>
                <p className="mt-2 text-gray-600">{c.subtitle}</p>

                {c.sections.map((s) => (
                    <Section key={s.title} title={s.title}>{s.body}</Section>
                ))}
            </article>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mt-10">
            <h2 className="font-display text-xl font-semibold text-[#00153f] mb-3">{title}</h2>
            <div className="text-gray-700 leading-relaxed space-y-1">{children}</div>
        </section>
    );
}
